import React, { Suspense, useEffect, useLayoutEffect, useState } from 'react'
import { PracticeApp, takePendingUpdateOr } from '@workspace/ui'
import { roleRegistry } from '../lib/roleRegistry'
import { selectedAppStore } from '@workspace/shared'
import { APP_KEY_BASE_PATHS } from '@dashboard/constants/appRegistry'
import { Spinner } from './Spinner'
import type { RoleKey } from '@workspace/ui'

const ROLE_ERROR_STORAGE_KEY = 'dashboard:lastRoleError'
const ENABLE_ROLE_REDIRECT = true // flip to true to restore redirect-on-role-error

const persistRoleError = (role: RoleKey, error: unknown) => {
  const payload = {
    role,
    message: error instanceof Error ? error.message : String(error),
    stack: error instanceof Error ? error.stack : undefined,
    at: Date.now(),
  }
  try {
    sessionStorage.setItem(ROLE_ERROR_STORAGE_KEY, JSON.stringify(payload))
  } catch (storageError) {
    console.warn('[Dashboard] Failed to persist role error', storageError)
  }
}


function InlineErrorBoundary({
  role,
  children,
}: {
  role: RoleKey
  children: React.ReactNode
}) {
  const [error, setError] = useState<Error | null>(null)
  const [shouldRedirect, setShouldRedirect] = useState(false)
  const [isOnline, setIsOnline] = useState<boolean>(
    typeof navigator === 'undefined' ? true : navigator.onLine,
  )

  useEffect(() => {
    const handler = (ev: ErrorEvent) => {
      // Offline: fetches (settings/games/profile) reject as expected. A
      // cached app should keep running from persisted state, so DON'T take
      // it down with the error cover — just log it.
      if (!navigator.onLine) {
        console.warn('[Dashboard] Ignoring error while offline', ev.error?.message || ev.message)
        return
      }
      console.error('[Dashboard] Uncaught error inside role app', {
        role,
        message: ev.error?.message || ev.message,
        stack: ev.error?.stack,
      })
      persistRoleError(role, ev.error ?? ev.message)
      setError(ev.error ?? new Error(ev.message))
      setShouldRedirect(true)
    }

    const rejection = (ev: PromiseRejectionEvent) => {
      if (!navigator.onLine) {
        console.warn('[Dashboard] Ignoring unhandled rejection while offline', ev.reason)
        return
      }
      console.error('[Dashboard] Unhandled rejection inside role app', {
        role,
        reason: ev.reason,
      })
      persistRoleError(role, ev.reason)
      setError(ev.reason instanceof Error ? ev.reason : new Error(String(ev.reason)))
      setShouldRedirect(true)
    }

    window.addEventListener('error', handler)
    window.addEventListener('unhandledrejection', rejection)
    return () => {
      window.removeEventListener('error', handler)
      window.removeEventListener('unhandledrejection', rejection)
    }
  }, [role])

  useEffect(() => {
    const onOnline = () => {
      // Back online after a crash — reload to fetch whatever couldn't load.
      if (error) {
        window.location.reload()
        return
      }
      setIsOnline(true)
    }
    const onOffline = () => setIsOnline(false)
    window.addEventListener('online', onOnline)
    window.addEventListener('offline', onOffline)
    return () => {
      window.removeEventListener('online', onOnline)
      window.removeEventListener('offline', onOffline)
    }
  }, [error])

  useEffect(() => {
    if (!ENABLE_ROLE_REDIRECT) return
    if (!shouldRedirect) return
    // Never bounce to the shell while offline — the reload would fail too.
    if (!isOnline) return
    // Long enough to read the update message before the redirect lands.
    // Onto the waiting build when there is one: a plain navigation would
    // stay on the old one, and fail on this role again.
    const timer = setTimeout(() => {
      takePendingUpdateOr(() => window.location.replace('/'))
    }, 4000)
    return () => clearTimeout(timer)
  }, [shouldRedirect, isOnline])

  if (error) {
    console.error('[Dashboard] Failed to load role app', {
      role,
      message: error?.message,
      stack: error?.stack,
      error,
    })
    // Same dark "update required" panel the in-game app shows on errors —
    // coaches see an update prompt, not a technical failure.
    return (
      <div className="flex h-screen w-screen flex-col items-center justify-center gap-4 bg-[#0d121c] px-6 text-center text-white">
        <p className="text-lg font-medium">
          A new update is required to load this page. Please click the button
          below to refresh.
        </p>
        <button
          onClick={() => takePendingUpdateOr(() => window.location.replace('/'))}
          className="rounded-md bg-blue-600 px-6 py-2 text-sm font-semibold text-white transition hover:bg-blue-700"
        >
          Reload App
        </button>
      </div>
    )
  }

  return (
    <ErrorBoundaryInner
      onError={(err) => {
        persistRoleError(role, err)
        setError(err)
        setShouldRedirect(true)
      }}
    >
      {children}
    </ErrorBoundaryInner>
  )
}

class ErrorBoundaryInner extends React.Component<
  { onError: (error: Error) => void; children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: any) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  componentDidCatch(error: Error) {
    this.props.onError(error)
  }

  render() {
    if (this.state.hasError) {
      return null
    }
    return this.props.children
  }
}

export function RoleHost({ role }: { role: RoleKey }) {
  const RoleComponent = roleRegistry[role]
  const shouldWrapWithShell = role !== 'qb'

  // Every request files under the app it comes from (x-app-selected). Only
  // the dashboard tile used to set it — and the dashboard clears it — so an
  // app reached any other way (Back from the dashboard, a bookmark, a typed
  // URL) read and saved under "Dashboard": the coach's games looked empty and
  // their reps were refused (2026-10-17). Set before the app's first request:
  // layout effects run before any effect of the (lazy) app below.
  useLayoutEffect(() => {
    const appKey = APP_KEY_BASE_PATHS[role]
    if (appKey && selectedAppStore.getState().appKey !== appKey) {
      selectedAppStore.getState().setSelectedAppState({ key: 'appKey', value: appKey })
    }
  }, [role])

  useLayoutEffect(() => {
    document.body.classList.add('dashboard-role-active')
    return () => {
      document.body.classList.remove('dashboard-role-active')
    }
  }, [])

  useEffect(() => {
    const raw = sessionStorage.getItem(ROLE_ERROR_STORAGE_KEY)
    if (!raw) return
    try {
      console.warn('[Dashboard] Previous role error', JSON.parse(raw))
    } catch (err) {
      console.warn('[Dashboard] Previous role error (unparsed)', raw, err)
    }
    sessionStorage.removeItem(ROLE_ERROR_STORAGE_KEY)
  }, [])

  useEffect(() => {
    sessionStorage.setItem('dashboardActiveRole', role)
  }, [role])

  // GoogleOAuthProvider is supplied by the shared AppGameLibraryProvider now,
  // so every role (not just qb/st) has it wherever the Game Library renders —
  // no per-role special-casing here.
  const wrapped = shouldWrapWithShell ? (
    <PracticeApp role={role}>
      <RoleComponent />
    </PracticeApp>
  ) : (
    <RoleComponent />
  )

  return (
    <InlineErrorBoundary role={role}>
      <Suspense fallback={<Spinner />}>{wrapped}</Suspense>
    </InlineErrorBoundary>
  )
}
